import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { DiscBadge, PriorityBadge } from "@/components/badges";
import { History, Search, MapPin, Image as ImageIcon, ScrollText, ShieldCheck, Loader2 } from "lucide-react";

const TABS = [
  { key: "timeline", label: "Timeline", icon: ScrollText },
  { key: "map", label: "Map", icon: MapPin },
  { key: "media", label: "Media", icon: ImageIcon },
  { key: "audit", label: "Audit", icon: ShieldCheck },
];
const SRC_COLOR = {
  CAD: "#3B82F6", MULTIMEDIA: "#22D3EE", "CALLER CHAT": "#A78BFA", TRANSCRIPT: "#F59E0B",
  "FIELD FORM": "#10B981", "CAD ATTACHMENT": "#6B7280",
};
const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "—");

export default function EventHistory() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [ws, setWs] = useState(null);
  const [tab, setTab] = useState("timeline");
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async (query) => {
    try { const { data } = await client.get("/workspace/incidents", { params: query ? { q: query } : {} }); setList(data || []); }
    catch { setList([]); }
  }, []);
  useEffect(() => { loadList(""); }, [loadList]);

  const open = async (inc) => {
    setSel(inc); setWs(null); setBusy(true);
    try { const { data } = await client.get(`/incidents/${inc.id}/workspace`); setWs(data); }
    catch { setWs({ error: true }); }
    setBusy(false);
  };

  const loc = ws?.incident?.location;
  const hasCoords = loc?.lat != null && loc?.lng != null;

  return (
    <Layout>
      <PageHeader title="Event History Workspace" sub="Unified timeline · map · media gallery · audit trail across the full incident record" icon={History} />
      <div className="flex-1 flex overflow-hidden" data-testid="event-history-page">
        {/* Incident picker */}
        <div className="w-80 shrink-0 border-r border-[#2C2D35] flex flex-col bg-[#0d0e11]">
          <div className="p-3 border-b border-[#2C2D35]">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-2.5 text-[#6B7280]" />
              <input data-testid="workspace-search" value={q}
                onChange={(e) => { setQ(e.target.value); loadList(e.target.value); }}
                placeholder="Search CC# · type · address…"
                className="w-full bg-[#141518] border border-[#2C2D35] pl-8 pr-3 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {list.map((i) => (
              <button key={i.id} data-testid={`workspace-inc-${i.cc_number}`} onClick={() => open(i)}
                className={`w-full text-left px-3 py-2.5 border-b border-[#1E1F24] hover:bg-[#141518] transition-colors ${sel?.id === i.id ? "bg-[#141518] border-l-2 border-l-[#3B82F6]" : ""}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] font-bold text-[#22D3EE]">{i.cc_number}</span>
                  <PriorityBadge priority={i.priority} />
                  <DiscBadge discipline={i.discipline} />
                  <span className="ml-auto font-mono text-[9px] uppercase text-[#6B7280]">{i.status}</span>
                </div>
                <div className="text-xs text-white truncate mt-0.5">{i.call_type_label}</div>
                <div className="font-mono text-[9px] text-[#6B7280] truncate">{i.location?.address || "No address"} · {fmtTs(i.created_at)}</div>
              </button>
            ))}
            {!list.length && <div className="p-4 font-mono text-[11px] text-[#6B7280]">No incidents match.</div>}
          </div>
        </div>

        {/* Workspace */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!sel && <div className="flex-1 flex items-center justify-center font-mono text-xs text-[#6B7280]">Select an incident to open its unified event history.</div>}
          {sel && busy && <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-[#3B82F6]" /></div>}
          {sel && ws && !busy && !ws.error && (
            <>
              <div className="shrink-0 px-4 py-3 border-b border-[#2C2D35] bg-[#0d0e11] flex items-center gap-3 flex-wrap">
                <span className="font-mono font-bold text-sm text-[#22D3EE]">{ws.incident.cc_number}</span>
                <span className="text-sm font-head">{ws.incident.call_type_label}</span>
                <span className="font-mono text-[10px] text-[#9CA3AF]">{ws.incident.location?.address}</span>
                <div className="ml-auto flex gap-1">
                  {TABS.map((t) => (
                    <button key={t.key} data-testid={`workspace-tab-${t.key}`} onClick={() => setTab(t.key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-head font-semibold uppercase rounded-sm border transition-colors ${tab === t.key ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "border-[#2C2D35] text-[#9CA3AF] hover:text-white"}`}>
                      <t.icon size={12} /> {t.label}
                      <span className="font-mono text-[9px] opacity-70">{t.key === "map" ? "" : ws.counts[t.key]}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {tab === "timeline" && (
                  <div className="space-y-0 max-w-4xl" data-testid="workspace-timeline">
                    {ws.timeline.map((e, i) => (
                      <div key={i} className="flex gap-3 group">
                        <div className="flex flex-col items-center">
                          <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: SRC_COLOR[e.source] || "#6B7280" }} />
                          {i < ws.timeline.length - 1 && <span className="w-px flex-1 bg-[#2C2D35]" />}
                        </div>
                        <div className="pb-3 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-sm border"
                              style={{ color: SRC_COLOR[e.source] || "#9CA3AF", borderColor: `${SRC_COLOR[e.source] || "#9CA3AF"}55` }}>{e.source}</span>
                            <span className="font-mono text-[10px] text-[#6B7280]">{fmtTs(e.ts)}</span>
                            {e.user && <span className="font-mono text-[10px] text-[#9CA3AF]">{e.user}</span>}
                          </div>
                          <div className="text-xs text-[#E5E7EB] mt-0.5 whitespace-pre-wrap">{e.detail}</div>
                        </div>
                      </div>
                    ))}
                    {!ws.timeline.length && <div className="font-mono text-[11px] text-[#6B7280]">No timeline entries recorded.</div>}
                  </div>
                )}
                {tab === "map" && (
                  <div data-testid="workspace-map" className="h-full min-h-[400px] border border-[#2C2D35] rounded-sm overflow-hidden">
                    {hasCoords ? (
                      <iframe title="incident-map" className="w-full h-full min-h-[400px]"
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${loc.lng - 0.008},${loc.lat - 0.005},${loc.lng + 0.008},${loc.lat + 0.005}&layer=mapnik&marker=${loc.lat},${loc.lng}`} />
                    ) : (
                      <div className="h-full flex items-center justify-center font-mono text-[11px] text-[#6B7280] min-h-[400px]">No coordinates recorded for this incident.</div>
                    )}
                  </div>
                )}
                {tab === "media" && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="workspace-media">
                    {ws.media.map((m, i) => (
                      <div key={i} className="border border-[#2C2D35] bg-[#141518] rounded-sm overflow-hidden">
                        {m.kind === "image" && m.url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api${m.url}`} alt={m.name} className="w-full h-32 object-cover" />
                        ) : (
                          <div className="h-32 flex items-center justify-center text-[#6B7280] font-mono text-[10px] uppercase">{m.kind || "file"}</div>
                        )}
                        <div className="px-2 py-1.5">
                          <div className="text-[11px] text-white truncate">{m.name}</div>
                          <div className="font-mono text-[9px] text-[#6B7280]">{m.source} · {m.by || ""} · {fmtTs(m.ts)}</div>
                        </div>
                      </div>
                    ))}
                    {!ws.media.length && <div className="font-mono text-[11px] text-[#6B7280] col-span-4">No media attached to this incident.</div>}
                  </div>
                )}
                {tab === "audit" && (
                  <table className="w-full text-left max-w-5xl" data-testid="workspace-audit">
                    <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase">
                      <tr><th className="px-2 py-2">Time</th><th className="px-2">Action</th><th className="px-2">User</th><th className="px-2">Detail</th></tr>
                    </thead>
                    <tbody>
                      {ws.audit.map((a, i) => (
                        <tr key={i} className="border-b border-[#1E1F24]">
                          <td className="px-2 py-1.5 font-mono text-[10px] text-[#6B7280] whitespace-nowrap">{fmtTs(a.ts)}</td>
                          <td className="px-2 font-mono text-[10px] font-bold uppercase text-[#F59E0B]">{a.kind}</td>
                          <td className="px-2 text-xs">{a.user || "—"}</td>
                          <td className="px-2 font-mono text-[10px] text-[#9CA3AF]">{a.detail}</td>
                        </tr>
                      ))}
                      {!ws.audit.length && <tr><td colSpan={4} className="px-2 py-3 font-mono text-[11px] text-[#6B7280]">No audit entries (views, exports, NCIC queries, corrections) recorded.</td></tr>}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
